import React, {useState, useEffect} from 'react';

import { Card, CardHeader, Drawer, IconButton, makeStyles, Typography } from '@material-ui/core';

import MoreVertIcon from '@material-ui/icons/MoreVert';
import CloudOffIcon from "@material-ui/icons/CheckCircle";
import StarIcon from '@material-ui/icons/Star';
import { LibraryItem } from '../../../../entities/LibraryItem';
import DateHelper from '../../../../utilities/DateHelper';
import { DisplayTextHelper } from '../../../../utilities/displayTextHelper';
import { LibraryItemSort } from '../../../../utilities/libraryItemSorter';
import { LibraryItemType } from '../../../../entities/LibraryItemType';
import { LinkAssetType } from '../../../../entities/LinkAssetType';
import { routes } from '../../../../routes';
import { FileAssetService } from '../../../../services/FIleAssetService';
import { useHistory } from 'react-router-dom';
import { ShowEmbeddedLinkDialog } from './ShowEmbeddedLinkDialog';
import { LibraryAssetActions } from '../../LibraryAssetActions/LibraryAssetActions';
import { LibraryItemIcon } from './LibraryItemIcon';
import { SortByDropDown, SortCatagories } from './SortByDropDown';
import { LoadingDialog } from '../../../../components/LoadingDialog';

const useStyles = makeStyles((theme) => ({
    cardContainer: {
        margin: "auto",
        marginBottom: "8px",
        marginTop: "8px",
        width: "90%"
    },
    cardHeaderTitle: {
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: "0.875rem",
        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        fontWeight: 400,
        letterSpacing: "0.01071em"
    },
    cardHeaderSubtitle: {
        color: "rgba(0, 0, 0, 0.54)",
        fontSize: "0.875rem",
        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        fontWeight: 400,
        letterSpacing: "0.01071em"
    },
    inlineIcon: {
        color: "rgb(143, 143, 143)",
        fontSize: "16px",
        verticalAlign: "text-top",
        marginLeft: "4px"
    },
    sectionTitle: {
        color: "#5f6368",
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: "Roboto, Helvetica",
        borderBottom: "1px solid rgb(225,225,225)",
        paddingTop: "10px",
        paddingBottom: "10px",
        [theme.breakpoints.down('sm')]: {
            borderTop: "1px solid rgb(225,225,225)",
            backgroundColor: "#FFF",
        },
        display: "flex",
        flexDirection: "row-reverse",
        paddingRight: 27,
        marginTop: 10
    },
}));

interface Props {
    items: LibraryItem[];
    focusedItem?: LibraryItem;
    setFocusedItemId: (id?:string) => void;
}

export function MobileLibraryAssetTable(props:Props){
    const { items, focusedItem } = props;
    const history = useHistory();
    const classes = useStyles();
    const {fromAssetCategory, fromLibraryItemType} = DisplayTextHelper;
    const { buildDateFormat } = DateHelper;
    
    const [downloading, setDownloading] = useState(false)
    const [displayItems, setDisplayItems] = useState<LibraryItem[]>(items);
    const [sortBy, setSortBy] = useState<SortCatagories>("Last Modified");
    const [sortDirection, ] = useState<"Ascending"|"Descending">("Ascending");
    const [showEmbeddedLinkDialog, setShowEmbeddedLinkDialog] = useState(false);
    const [showActionMenu, setShowActionMenu] = useState(false);

    useEffect(() => {
        const sortedItems = LibraryItemSort(sortBy, [...items], sortDirection);
        setDisplayItems(sortedItems)
    }, [items, sortBy, sortDirection])

    const openActionMenu = (e: React.MouseEvent<HTMLElement>, item: LibraryItem) => {
        e.preventDefault();
        e.stopPropagation();
        props.setFocusedItemId(item.id);
        setShowActionMenu(true);
    }

    const handleClick = async(item: LibraryItem) => {
        switch (item.type) {
            case LibraryItemType.BuilderAsset:
                return history.push(routes.app.resolve.builderPage(item.id), { prevUrl: window.location.href });
                
            case LibraryItemType.LinkAsset: {
                props.setFocusedItemId(item.id)
				if (item.linkType! === LinkAssetType.General && item.linkUrl != null) {
					const redirectUrl = item.linkUrl.match(/^https?:\/\//i) ? item.linkUrl : "//" + item.linkUrl;
					return window.open(redirectUrl, "_blank");
				} else {
					return setShowEmbeddedLinkDialog(true)
				}
			}

			case LibraryItemType.FileAsset: {
                setDownloading(true);                
                await FileAssetService.downloadFile(item.id, item.name)
                setDownloading(false);   
                return;             
            }

			default:
                return history.push(routes.app.resolve.libraryFolder(item.id))
		}
    }

    const renderAssetCard = (item:LibraryItem) => {
        return (
            <Card className={classes.cardContainer} square={true} key={item.id} onClick={() => handleClick(item)}>
                <CardHeader
                    avatar={<LibraryItemIcon itemType={item.type} isTemplate={item.isTemplate} linkType={item.linkType} />}
                    title={
                        <Typography
                        variant="body1"
                        className={classes.cardHeaderTitle}
                        >
                            {item.name} {item.isTemplate && <> - <strong>Custom Template</strong></>}
                        </Typography>
                    }
                    disableTypography
                    subheader={
                        <>
                            <Typography
                                variant="body1"
                                className={classes.cardHeaderSubtitle}
                                >
                                {item.category ? fromAssetCategory(item.category) : fromLibraryItemType(item.type)}
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.cardHeaderSubtitle}
                                >
                                {item.isStarred && <StarIcon className={classes.inlineIcon} />} {item.offlineMode && <CloudOffIcon className={classes.inlineIcon}/>} {buildDateFormat(item.dateModified)}
                            </Typography>
                        </>
                    }
                    action={
                        <IconButton onClick={(e) => openActionMenu(e, item)}>
                            <MoreVertIcon />
                        </IconButton>}
                />
            </Card>
        )
    }

    return(
        <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
                <SortByDropDown sortBy={sortBy} sortPremiumContent={false} handleSortByChange={(sort) => setSortBy(sort)}/>
            </Typography>
            <div style={{paddingBottom: 50}}>
                { displayItems.map(renderAssetCard) }
            </div>
            { focusedItem && focusedItem.type === LibraryItemType.LinkAsset &&
                <ShowEmbeddedLinkDialog 
                    open={showEmbeddedLinkDialog} 
                    onClose={() => setShowEmbeddedLinkDialog(false)}
                    linkUrl={focusedItem.linkUrl!}
                    linkType={focusedItem.linkType!}
                />
            }
            { focusedItem &&
                <Drawer anchor='bottom' open={showActionMenu} onClose={() => setShowActionMenu(false)}>
                    <LibraryAssetActions focusedItem={focusedItem} onClose={() => setShowActionMenu(false)} />
                </Drawer>
            }
            { downloading && <LoadingDialog text='Downloading' />}
        </>
    )
}